import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Sparkles,
    ChevronUp,
    Calendar,
    BookOpen,
    Wrench,
    Brain,
    Server,
    ShieldCheck,
    Globe2,
    ArrowUpRight,
} from "lucide-react";
import { fetchRoadmap } from "../lib/cmsApi";

const COLUMNS = [
    { id: "now", title: "Now", sub: "shipping this quarter", accent: "text-emerald-300", chip: "bg-emerald-300/10 border-emerald-300/25", ring: "from-emerald-400/30 to-emerald-400/0" },
    { id: "next", title: "Next", sub: "in design or in build", accent: "text-cyan-300", chip: "bg-cyan-300/10 border-cyan-300/25", ring: "from-cyan-400/30 to-cyan-400/0" },
    { id: "later", title: "Later", sub: "on the horizon", accent: "text-violet-300", chip: "bg-violet-300/10 border-violet-300/25", ring: "from-violet-400/30 to-violet-400/0" },
];

const PILL = {
    intelligence: { label: "Intelligence", icon: Brain, color: "text-rose-300" },
    projects: { label: "Projects", icon: Wrench, color: "text-amber-glow" },
    sales: { label: "Sales", icon: Sparkles, color: "text-cyan-300" },
    security: { label: "Security", icon: ShieldCheck, color: "text-rose-300" },
    platform: { label: "Platform", icon: Server, color: "text-electric" },
    global: { label: "Global", icon: Globe2, color: "text-emerald-300" },
};

const FALLBACK_SEED = [
    { id: "co-pilot-actions", col: "now", area: "intelligence", title: "Co-pilot can act, not just answer", desc: "Approved automations executed directly by the Co-pilot — every action audited and reversible.", votes: 612, eta: "Mar 2026" },
    { id: "projects-gantt-v2", col: "now", area: "projects", title: "Projects timeline v2", desc: "Drag-resize bars, nested phases and full-quarter zoom — without the lag.", votes: 401, eta: "Mar 2026" },
    { id: "scim", col: "now", area: "security", title: "SCIM provisioning", desc: "Auto-create / suspend users from any SCIM-compliant IdP (Okta, Entra, JumpCloud).", votes: 318, eta: "Mar 2026" },

    { id: "sales-orchestration", col: "next", area: "sales", title: "Account-based orchestration", desc: "Treat accounts as the unit, not deals — multi-thread plays across stakeholders.", votes: 287 },
    { id: "platform-byo-model", col: "next", area: "platform", title: "BYO model (BYO-LLM)", desc: "Bring your own GPT, Claude or Gemini key per workspace. Enterprise-only.", votes: 246 },
    { id: "intel-anomaly", col: "next", area: "intelligence", title: "Anomaly explanations", desc: "When something moves outside its normal band, the Co-pilot tells you why and proposes a next step.", votes: 219 },

    { id: "global-residency-india", col: "later", area: "global", title: "India tenancy region", desc: "Tenant pinned to ap-south-1 with in-country backups for regulated industries.", votes: 188 },
    { id: "mobile-native", col: "later", area: "platform", title: "Native mobile (iOS + Android)", desc: "Standalone mobile apps — read, approve, log, escalate. Offline-first.", votes: 174 },
    { id: "marketplace", col: "later", area: "platform", title: "Marketplace v1", desc: "Public marketplace for workflows, SOPs and dashboards built by the community.", votes: 142 },
    { id: "languages", col: "later", area: "global", title: "Localisation: 12 languages", desc: "Full UI translation for ES, FR, DE, PT, JA, KO, ZH, AR, HI, IT, NL, TR.", votes: 121 },
];

export default function RoadmapPage() {
    const [voted, setVoted] = useState({});
    const [items, setItems] = useState(FALLBACK_SEED);

    useEffect(() => {
        fetchRoadmap()
            .then((data) => {
                if (data?.items?.length) setItems(data.items);
            })
            .catch(() => {});
    }, []);

    const toggle = (id) => setVoted((v) => ({ ...v, [id]: !v[id] }));

    return (
        <div className="pt-28 lg:pt-32" data-testid="roadmap-page">
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // public roadmap · ship in the open
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="roadmap-title"
                        >
                            What we&apos;re building.
                            <br />
                            <span className="gradient-text">In plain sight.</span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        Three columns. No vapor. Vote on what you&apos;d like
                        sooner — the most-voted items move into{" "}
                        <span className="text-emerald-300 font-semibold">
                            Now
                        </span>{" "}
                        in the next quarterly review.
                    </p>
                </div>
            </section>

            <section className="relative pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {COLUMNS.map((col) => {
                        const colItems = items.filter((s) => s.col === col.id);
                        return (
                            <div
                                key={col.id}
                                className="relative rounded-2xl border border-white/[0.08] bg-obsidian-200/50 backdrop-blur-sm p-5"
                                data-testid={`roadmap-col-${col.id}`}
                            >
                                <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${col.ring} blur-3xl pointer-events-none`} />
                                <div className="relative flex items-center justify-between mb-5">
                                    <div>
                                        <span
                                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 ${col.chip}`}
                                        >
                                            <span
                                                className={`w-1.5 h-1.5 rounded-full ${col.accent.replace("text-", "bg-")} animate-pulse-glow`}
                                            />
                                            <span
                                                className={`text-[10px] font-code uppercase tracking-[0.18em] font-semibold ${col.accent}`}
                                            >
                                                {col.title}
                                            </span>
                                        </span>
                                        <p className="text-[11px] text-zinc-500 font-code mt-1.5">
                                            {col.sub} · {colItems.length} items
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    {colItems.map((it, i) => {
                                        const P = PILL[it.area];
                                        const isVoted = !!voted[it.id];
                                        return (
                                            <motion.article
                                                key={it.id}
                                                initial={{ opacity: 0, y: 12 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, margin: "-30px" }}
                                                transition={{ duration: 0.35, delay: i * 0.04 }}
                                                whileHover={{ y: -3 }}
                                                className="group relative rounded-xl border border-white/[0.06] bg-obsidian-300/60 p-4 overflow-hidden"
                                                data-testid={`roadmap-card-${it.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-3 mb-3">
                                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.06] bg-white/[0.02] px-2 py-0.5">
                                                        <P.icon
                                                            className={`w-3 h-3 ${P.color}`}
                                                        />
                                                        <span className="text-[10px] font-code uppercase tracking-[0.18em] text-zinc-400">
                                                            {P.label}
                                                        </span>
                                                    </span>
                                                    <button
                                                        onClick={() => toggle(it.id)}
                                                        data-testid={`roadmap-vote-${it.id}`}
                                                        className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition ${
                                                            isVoted
                                                                ? "bg-electric/15 border-electric/40 text-electric"
                                                                : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:text-white hover:bg-white/[0.06]"
                                                        }`}
                                                        aria-label={`Vote for ${it.title}`}
                                                    >
                                                        <ChevronUp className="w-3 h-3" />
                                                        <span className="tabular-nums font-code">
                                                            {it.votes +
                                                                (isVoted ? 1 : 0)}
                                                        </span>
                                                    </button>
                                                </div>
                                                <h3 className="font-outfit text-sm font-semibold text-white leading-snug">
                                                    {it.title}
                                                </h3>
                                                <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed line-clamp-3">
                                                    {it.desc}
                                                </p>
                                                {it.eta && (
                                                    <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-code text-zinc-500">
                                                        <Calendar className="w-2.5 h-2.5" />
                                                        target · {it.eta}
                                                    </div>
                                                )}
                                            </motion.article>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="max-w-7xl mx-auto px-6 sm:px-8 mt-12">
                    <div className="rounded-2xl border border-white/[0.08] bg-obsidian-200/60 p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-glow mb-1">
                                // missing something?
                            </p>
                            <h3 className="font-outfit text-lg text-white font-semibold">
                                Suggest a feature — we read every one.
                            </h3>
                            <p className="text-xs text-zinc-500 mt-1">
                                Customer-driven items ship monthly.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="https://discord.gg/apexbrain"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-5 py-2.5 rounded-full hover:bg-white/10 transition"
                            >
                                Suggest on Discord
                                <ArrowUpRight className="w-4 h-4 text-electric" />
                            </a>
                            <a
                                href="/changelog"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-5 py-2.5 rounded-full hover:brightness-110 transition"
                            >
                                <BookOpen className="w-4 h-4" />
                                See what shipped
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
